import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { Order } from '~/graphql/graphql';

const POLL_INTERVAL_MS = 10_000;
const POLL_WINDOW_MS = 2 * 60_000;
const REFRESH_POLL_INTERVAL_MS = 3_000;
const REFRESH_POLL_TIMEOUT_MS = 20_000;

// Resets the poll window whenever orderId changes, since OrderViewContent isn't remounted by
// React Router on a param-only navigation and would otherwise carry over an already-expired
// deadline from the previously viewed order.
export function useMsrSyncRefetchInterval(enabled: boolean, orderId: string) {
  const deadlineRef = useRef<number | null>(null);
  const lastOrderIdRef = useRef<string | null>(null);
  if (lastOrderIdRef.current !== orderId) {
    lastOrderIdRef.current = orderId;
    deadlineRef.current = null;
  }
  if (enabled) {
    deadlineRef.current ??= Date.now() + POLL_WINDOW_MS;
  } else {
    deadlineRef.current = null;
  }
  return () =>
    enabled && deadlineRef.current !== null && Date.now() < deadlineRef.current
      ? POLL_INTERVAL_MS
      : false;
}

export function useMsrSyncToast(
  orderId: string,
  updatedAt: string | null | undefined,
  purchaseOrderNumber: string | null | undefined,
  fetchedAt: number
) {
  const { t } = useTranslation('order');
  const seenRef = useRef<string | null | undefined>(undefined);
  const seenFetchedAtRef = useRef(fetchedAt);
  const lastOrderIdRef = useRef<string | null>(null);

  useEffect(() => {
    const isFetched = fetchedAt !== seenFetchedAtRef.current;
    seenFetchedAtRef.current = fetchedAt;
    if (lastOrderIdRef.current !== orderId) {
      lastOrderIdRef.current = orderId;
      seenRef.current = undefined;
    }
    if (!purchaseOrderNumber) {
      return;
    }
    if (seenRef.current === undefined) {
      seenRef.current = updatedAt ?? null;
      return;
    }
    if (updatedAt && updatedAt !== seenRef.current) {
      seenRef.current = updatedAt;
      if (isFetched) {
        toast(t('order.msr_sync_updated'));
      }
    }
  }, [orderId, updatedAt, purchaseOrderNumber, fetchedAt, t]);
}

// A sync that finds nothing new still records when MSR was read, and creating the purchase order
// changes the order without reading it, so either one shows that the sync landed.
export function msrSyncMarker(
  order: Pick<Order, 'updatedAt' | 'msrSyncedAt'> | null | undefined
): string | undefined {
  return order
    ? `${order.updatedAt ?? ''}|${order.msrSyncedAt ?? ''}`
    : undefined;
}

/**
 * Drives the short active poll after a manual "Refresh" click, since the sync itself runs
 * asynchronously on a worker: `startRefreshing` arms polling until `syncMarker` (see
 * `msrSyncMarker`) changes or `REFRESH_POLL_TIMEOUT_MS` elapses, whichever comes first.
 */
export function useMsrRefreshPolling(syncMarker: string | null | undefined) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [seenSyncMarker, setSeenSyncMarker] = useState(syncMarker);

  if (isRefreshing && syncMarker !== seenSyncMarker) {
    setSeenSyncMarker(syncMarker);
    setIsRefreshing(false);
  }

  useEffect(() => {
    if (!isRefreshing) {
      return;
    }
    const timeout = setTimeout(
      () => setIsRefreshing(false),
      REFRESH_POLL_TIMEOUT_MS
    );
    return () => clearTimeout(timeout);
  }, [isRefreshing]);

  const startRefreshing = () => {
    setSeenSyncMarker(syncMarker);
    setIsRefreshing(true);
  };

  return {
    isRefreshing,
    startRefreshing,
    refetchInterval: () => (isRefreshing ? REFRESH_POLL_INTERVAL_MS : false),
  };
}
